import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Button } from './ui';

/**
 * Re-read ONE card, from wherever that card is drawn.
 *
 * Summon asks the chassis and the ACS for everything the page shows and the
 * operator waits on all of it to see one card (operator, 2026-09-09: "if you
 * just pull one, it is easier"). So every card that comes from a live read
 * carries one of these: the WAN card re-reads the WAN, the hosts card the
 * hosts, the OLT card the chassis. `run` is the read; `invalidate` is what to
 * redraw once it lands. The button spins while the read is out, and stops the
 * click from reaching a collapsible header it may be sitting in.
 */
export function RefreshButton({
  label,
  run,
  invalidate,
  onNotice,
  size = 'sm',
}: {
  /** What is re-read, for the tooltip and the toast: "the WAN", "connected hosts". */
  label: string;
  run: () => Promise<unknown>;
  invalidate: QueryKey[];
  onNotice?: (message: string, tone?: 'info' | 'danger') => void;
  /** `xs` is a 24px icon-only control for a card header that must stay one line tall. */
  size?: 'xs' | 'sm' | 'md';
}) {
  const queryClient = useQueryClient();
  const refresh = useMutation({
    mutationFn: run,
    onSuccess: async (result) => {
      await Promise.all(invalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      const said =
        result && typeof result === 'object' && 'message' in result
          ? String((result as { message: unknown }).message)
          : null;
      onNotice?.(said ?? `Re-read ${label}.`);
    },
    onError: (err) =>
      onNotice?.(err instanceof Error ? err.message : `Could not re-read ${label}`, 'danger'),
  });

  if (size === 'xs') {
    return (
      <button
        type="button"
        disabled={refresh.isPending}
        title={`Re-read ${label}`}
        aria-label={`Re-read ${label}`}
        onClick={(e) => {
          e.stopPropagation();
          refresh.mutate();
        }}
        className="inline-flex size-6 items-center justify-center rounded-md text-muted transition hover:bg-surface-2 hover:text-ink disabled:opacity-60"
      >
        <RefreshCw className={`size-3.5 ${refresh.isPending ? 'animate-spin' : ''}`} />
      </button>
    );
  }

  return (
    <Button
      size={size}
      variant="ghost"
      icon={<RefreshCw className={`size-3.5 ${refresh.isPending ? 'animate-spin' : ''}`} />}
      disabled={refresh.isPending}
      title={`Re-read ${label}`}
      aria-label={`Re-read ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        refresh.mutate();
      }}
    />
  );
}
